import { stats } from '@fiverr-private/obs';
import { useBriefCreationContext } from '../context/BriefCreationContext';
import { editBrief, mapBriefToEditRequestData } from '../api';
import { logger } from '../utils/logger';
import { METRICS_PREFIX } from '../constants';
import { mixpanelEvents } from '../monitoring';

interface UseEditBriefParams {
    briefId: string;
    onSuccess?: () => void;
    onError?: (error: Error) => void;
    fromEditSession?: boolean;
}

export const useEditBrief = ({ briefId, onSuccess, onError, fromEditSession = false }: UseEditBriefParams) => {
    const { brief, currency, setIsBriefLoading, isBriefLoading, variant } = useBriefCreationContext();

    const handleSubmitError = (error: Error) => {
        logger.error(new Error('Failed to edit brief'), { cause: error });
        stats.count(METRICS_PREFIX, 'edit_brief.error');
        onError?.(error);
    };

    const handleSubmit = async (requestExtraWave: boolean): Promise<void> => {
        if (!brief) {
            const error = new Error('Cannot edit brief: brief data is missing');
            handleSubmitError(error);
            return;
        }

        setIsBriefLoading(true);
        const submissionStartTime = Date.now();
        const extra = fromEditSession ? { fromEditSession: true } : undefined;

        try {
            const requestData = mapBriefToEditRequestData(brief, currency);

            await editBrief({
                briefId,
                requestData,
                requestExtraWave,
            });

            stats.count(METRICS_PREFIX, 'edit_brief.success');
            mixpanelEvents.seamless(variant).editBrief.success(extra);
            onSuccess?.();
        } catch (error) {
            handleSubmitError(error as Error);
            throw error;
        } finally {
            setIsBriefLoading(false);
            stats.time(METRICS_PREFIX, 'edit_brief.duration', Date.now() - submissionStartTime);
        }
    };

    return {
        isBriefLoading,
        handleSubmit,
    };
};
